import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Select, MenuItem,
  CircularProgress, Alert, SelectChangeEvent, Card, CardContent, Divider
} from '@mui/material';
import { kpiService, KPIMetrics } from '../services/kpiService';

// Funzione helper per formattare i nomi delle metriche
const formatKey = (key: string) => key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

// Componente riutilizzabile per visualizzare una card di metriche
const KpiCard: React.FC<{ title: string, data: { [key: string]: number } }> = ({ title, data }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Card elevation={3} sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>{title}</Typography>
        <Divider sx={{ mb: 2 }} />
        {Object.entries(data).map(([key, value]) => (
          <Box key={key} display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">{formatKey(key)}</Typography>
            <Typography variant="body1">
              <strong>
                {key.includes('eur') || key.includes('costo') ? `€ ${value.toFixed(2)}` : value}
              </strong>
            </Typography>
          </Box>
        ))}
      </CardContent>
    </Card>
  </Grid>
);

const KPIMetricsPage: React.FC = () => {
  const [metrics, setMetrics] = useState<KPIMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState('sempre');

  const fetchKPIs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await kpiService.getKPIMetrics(period);
      setMetrics(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore nel caricamento dei KPI.');
    } finally {
      setLoading(false);
    }
  }, [period]); // La fetch viene rieseguita solo quando cambia il periodo

  useEffect(() => {
    fetchKPIs();
  }, [fetchKPIs]);

  const handlePeriodChange = (event: SelectChangeEvent<string>) => {
    setPeriod(event.target.value);
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Metriche e KPI</Typography>
        <Select size="small" value={period} onChange={handlePeriodChange} sx={{ minWidth: 200 }}>
          <MenuItem value="sempre">Sempre</MenuItem>
          <MenuItem value="oggi">Oggi</MenuItem>
          <MenuItem value="ieri">Ieri</MenuItem>
          <MenuItem value="ultimi_7_giorni">Ultimi 7 giorni</MenuItem>
          <MenuItem value="questo_mese">Questo mese</MenuItem>
        </Select>
      </Box>
      
      {loading && (
        <Box display="flex" justifyContent="center" mt={5}>
          <CircularProgress />
        </Box>
      )}
      
      {error && <Alert severity="error">{error}</Alert>}
      
      {metrics && !loading && (
        <Grid container spacing={3}>
          <KpiCard title="📊 Valori Totali" data={metrics.valori_totali} />
          <KpiCard title="🎯 Conteggi Azioni" data={metrics.conteggi_azioni} />
          <KpiCard title="📈 Medie" data={metrics.medie} />
        </Grid>
      )}
    </Box>
  );
};

export default KPIMetricsPage;